import AssignTemplateIcon from "@assets/icons/assign-template.svg";
import { delay } from "@core/async/promises";
import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { getStartedManager, templateOperationsManager } from "@core/service-container";
import { getProgramDetails } from "@domain/program/program";
import { Template } from "@domain/template/template";
import { sendAnalyticsEventAssignProgram } from "@domain/template/template-analytics";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { TextAreaWithCharLimit } from "@ui/components/common/text-area-with-char-limit";
import { RoutesPath } from "@ui/navigation/routes";
import { PatientAutocomplete, PatientWithName } from "@ui/pages/program/dialog/components/patient-autocomplete";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { RestrictedDialog } from "@ui/components/dialog/restricted-dialog";
import { DIALOG_SIZE } from "@ui/components/dialog/base-dialog";

interface FormData {
	name: string;
	patient: PatientWithName;
}

export interface AssignTemplateDialogProps extends DialogProps {
	template: Template;
	onCancel: () => void;
}

export const AssignTemplateDialog: React.FC<AssignTemplateDialogProps> = ({
	template,
	onCancel,
	onHide,
	...otherProps
}) => {
	const { format } = useI18n();
	const navigate = useNavigate();

	const { control, handleSubmit } = useForm<FormData>({
		defaultValues: { name: template.name, patient: undefined },
	});

	const [submittedPatient, setSubmittedPatient] = useState<PatientWithName | null>(null);

	const [assignTemplate, loadingSubmit] = useAsyncOperation(
		async ({ patient: selectedPatient, name }: FormData) => {
			try {
				setSubmittedPatient(selectedPatient);

				const days = template.days;
				const newProgram = {
					name,
					days,
				};
				const [programMetadata] = await Promise.all([
					templateOperationsManager.performAssignOperation(selectedPatient.id, newProgram),
					getStartedManager.tryMarkProgramsStepAsCompleted(),
					delay(3000),
				]);
				const exerciseDetails = getProgramDetails(days);
				sendAnalyticsEventAssignProgram(selectedPatient.analyticsId, programMetadata.id, days, exerciseDetails);
				navigate(RoutesPath.ProgramBuilder, {
					route: { patientId: selectedPatient.id },
					query: { programId: programMetadata.id },
				}, { replace: true });
				onHide();
			} catch (e) {
				console.error("Failure to assign template to patient", e);
			}
		},
		{ deps: [navigate, otherProps, template] },
	);

	useEffect(() => {
		if (!loadingSubmit) {
			setSubmittedPatient(null);
		}
	}, [loadingSubmit]);

	return (
		<RestrictedDialog
			onHide={() => {
				onHide();
				onCancel();
			}}
			context="AssignTemplate"
			header={format("templateBuilder.assignTemplateDialog.title")}
			footer={
				loadingSubmit && submittedPatient ? (
					<></>
				) : (
					<ButtonContainer>
						<Button
							label={format("templateBuilder.assignTemplateDialog.submitButton")}
							onClick={handleSubmit(assignTemplate)}
							type="submit"
						/>
					</ButtonContainer>
				)
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			<DialogContainer>
				<Form onSubmit={handleSubmit(assignTemplate)}>
					<FormContainer>
						<FormField>
							<label className="p-size-m">{format("programBuilder.startDialog.programName.label")}</label>
							<Controller
								name="name"
								control={control}
								rules={{
									required: format("global.form.field.requiredError"),
									maxLength: {
										value: APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH,
										message: format("global.programName.maxLength"),
									},
								}}
								render={({ field: { onChange, value, ...otherFields }, fieldState }) => (
									<TextAreaWithCharLimit
										{...otherFields}
										autoComplete="off"
										autoFocus={!value}
										text={value}
										onTextChange={onChange}
										errorMessage={fieldState.error?.message}
										charLimit={APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH}
										placeholder={format("programBuilder.startDialog.programName.placeholder")}
									/>
								)}
							/>
						</FormField>
						<FormField>
							<label className="p-size-m">{format("templateBuilder.assignTemplateDialog.patientInput.label")}</label>
							<PatientAutocomplete control={control} name={"patient"} />
						</FormField>
					</FormContainer>
				</Form>
			</DialogContainer>
			{loadingSubmit && submittedPatient && (
				<LoadingCover>
					<div className="p-size-l" style={{ textAlign: "center" }}>
						{format("templateBuilder.assignTemplateDialog.loading.v2_6", { name: submittedPatient.name })}
					</div>
					<LoadingContainer style={{ height: "unset" }} />
					<AssignTemplateIcon width={300} height={184} />
				</LoadingCover>
			)}
		</RestrictedDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")};
	height: 100%;
`;

const ButtonContainer = styled.div`
	${row("S", "flexend")}
`;

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")}
	height: 100%;
`;

const FormContainer = styled.div`
	${stack("M", "flexstart", "stretch")}
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;

const LoadingCover = styled.div`
	position: absolute;
	inset: 0;
	background-color: ${colors.white};
	border-radius: 1rem;
	padding: 32px 40px;
	height: 100%;
	${stack(32, "center", "center")};
`;
